import { useMemo, useState } from 'react'
import { Button, Dialog } from '@/components/common'
import { lineupReasonLabel, packParticipantSummary } from './prepackLineupModel'

/**
 * A pin or replacement the limits refuse (#990): the subject at its limit
 * and the shown packs holding it, each with Replace (when shown at a
 * position) and Exclude from surface, so the operator resolves it rather
 * than the limit giving way. Unpinning a holder is no resolution: it stays
 * shown, so it still counts.
 */
export function LineupConflictDialog({ conflict, packsById, positions, busy, onReplace, onExclude, onClose }) {
  return (
    <Dialog open={Boolean(conflict)} title="This would go past a limit" description={conflict?.message} onClose={onClose} footer={<Button variant="secondary" onClick={onClose}>Keep as is</Button>}>
      <ul className="flex flex-col gap-2 text-xs">
        {(conflict?.holders || []).map((id) => {
          const pack = packsById.get(id)
          return (
            <li key={id} className="flex items-center justify-between gap-2 rounded border border-[var(--wf-color-border)] px-3 py-2">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{pack?.title || 'Pack'}</span>
                <span className="block truncate text-[var(--wf-color-text-muted)]">{packParticipantSummary(pack)}</span>
              </span>
              <span className="flex shrink-0 gap-2">
                {positions.has(id) ? <Button size="compact" variant="secondary" disabled={busy} onClick={() => onReplace(id)}>Replace</Button> : null}
                <Button size="compact" variant="secondary" disabled={busy} onClick={() => onExclude(id)}>Exclude from surface</Button>
              </span>
            </li>
          )
        })}
      </ul>
    </Dialog>
  )
}

/**
 * Replace (#990): another eligible pack for a live position, chosen from the
 * available pool, or the one the surface's mix would choose.
 */
export function ReplaceSlotDialog({ target, available, busy, onChoose, onClose }) {
  const [search, setSearch] = useState('')
  const rows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return (available || []).filter(({ pack }) => !query || pack.title?.toLowerCase().includes(query) || packParticipantSummary(pack, Infinity).toLowerCase().includes(query))
  }, [available, search])
  return (
    <Dialog
      open={Boolean(target)}
      title={target ? `Replace #${target.position}` : 'Replace'}
      description={target ? `Choose a pack to show instead of ${target.pack.title}. It goes back to the available pool.` : undefined}
      onClose={onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => onChoose(null)} loading={busy}>Let the mix choose</Button>
        </>
      )}
    >
      <input type="search" aria-label="Search available packs" placeholder="Search available packs…" value={search} onChange={(event) => setSearch(event.target.value)} className="mb-3 w-full rounded border border-[var(--wf-color-border)] bg-transparent px-3 py-2 text-sm" />
      <ul aria-label="Available packs" className="flex max-h-[360px] flex-col gap-1 overflow-y-auto text-xs">
        {rows.length === 0 ? <li className="py-4 text-center text-[var(--wf-color-text-muted)]">{search ? 'No available pack matches.' : 'No other eligible pack is available.'}</li> : null}
        {rows.map(({ pack, reason, subject }) => (
          <li key={pack.id}>
            <button type="button" disabled={busy} onClick={() => onChoose(pack.id)} className="grid w-full grid-cols-[minmax(0,1fr)_64px] items-center gap-2 rounded border border-[var(--wf-color-border)] px-3 py-2 text-left hover:border-[var(--wf-color-action)] disabled:opacity-50">
              <span className="min-w-0">
                <span className="block truncate font-semibold">{pack.title}</span>
                <span className="block truncate text-[var(--wf-color-text-muted)]">{packParticipantSummary(pack)} · {lineupReasonLabel('available', reason, subject)}</span>
              </span>
              <span className="text-right font-semibold tabular-nums text-[var(--wf-color-success)]">{pack.combinedOdds}</span>
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  )
}
